/**
 * "Named on" (2 Oct): the requests someone else raised that name the reader in
 * a person field — the starter's manager on HR's New User Request, say — and
 * the notice on such a request's page.
 *
 * The reader may follow these and comment, nothing more; the server already
 * strips every other operation from `permitted_operations`, so the detail page
 * hides Close, attachments and answering without knowing why.
 */

import { Icon, Users } from "@norican/icons";
import { Alert, Badge, Typography } from "@norican/react";
import { useCallback } from "react";
import { Link } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  isRequesterUnresolvedResult,
  LoadingRows,
  RelativeTime,
  ResultStateNotice,
} from "@/components/common";
import { useT } from "@/i18n";

import type { NamedOnTicketSummary, TicketDetail } from "../api/contract";
import { useNamedOnTickets, useTranslateTo } from "../api/queries";
import { humaniseParameterName, namedOnRoleKey } from "../lib/named-on";
import { ticketReferenceKey } from "../lib/ticket-display";
import { TicketKindBadge, TicketStatusBadge } from "./ticket-badges";
import { TranslatableTextView } from "./translatable-text";

/** The translated role for a person field, or a readable stand-in for an unknown one. */
export function useNamedOnRoleLabel(): (parameterName: string) => string {
  const { t } = useT("tickets");
  return useCallback(
    (parameterName: string) => {
      const key = namedOnRoleKey(parameterName);
      return key !== null ? t(key) : humaniseParameterName(parameterName);
    },
    [t],
  );
}

export interface NamedOnBadgeProps {
  namedAs: string;
}

/** "You're named as: Reporting to". */
export function NamedOnBadge({ namedAs }: NamedOnBadgeProps) {
  const { t } = useT("tickets");
  const roleLabel = useNamedOnRoleLabel();
  return (
    <Badge tone="info" variant="subtle" size="xs">
      {t("namedOn.badge", { role: roleLabel(namedAs) })}
    </Badge>
  );
}

export interface NamedOnNoticeProps {
  ticket: TicketDetail;
}

/** Why this reader sees someone else's request, and what they may do with it. */
export function NamedOnNotice({ ticket }: NamedOnNoticeProps) {
  const { t } = useT("tickets");
  const roleLabel = useNamedOnRoleLabel();
  if (ticket.named_as == null) {
    return null;
  }
  return (
    <Alert tone="info" title={t("namedOn.noticeTitle")}>
      {t("namedOn.notice", {
        role: roleLabel(ticket.named_as),
        customer: ticket.requester.display_name,
      })}
    </Alert>
  );
}

/** The "Named on" tab of My tickets. Read only while the tab is open. */
export function NamedOnList() {
  const { t } = useT("tickets");
  const translateTo = useTranslateTo();
  const query = useNamedOnTickets(translateTo);
  const rows = query.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <ResultStateNotice result={query.data?.result} />
      {query.data?.partial_translation === true ? (
        <Alert tone="info">{t("translated.partial")}</Alert>
      ) : null}

      {query.isPending ? (
        <LoadingRows rows={3} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        // The notice above already explains a person Ivanti does not know;
        // an empty state under it would contradict it.
        isRequesterUnresolvedResult(query.data?.result) ? null : (
          <EmptyStateBlock
            icon={<Icon icon={Users} size="lg" tone="accent" />}
            title={t("namedOn.empty.title")}
            description={t("namedOn.empty.description")}
          />
        )
      ) : (
        <ul className="flex flex-col gap-2" aria-label={t("namedOn.listLabel")}>
          {rows.map((row) => (
            <li key={row.ticket_id}>
              <NamedOnCard row={row} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface NamedOnCardProps {
  row: NamedOnTicketSummary;
}

function NamedOnCard({ row }: NamedOnCardProps) {
  const { t } = useT("tickets");
  return (
    <Link
      to={row.url_path}
      className="border-border-subtle hover:bg-surface-hover focus-visible:ring-accent flex min-w-0 flex-col gap-2 rounded-md border p-3 focus-visible:ring-2 focus-visible:outline-none"
    >
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="text-accent-fg-subtle font-mono text-sm font-semibold tracking-tight">
          {t(ticketReferenceKey(row.kind, "id"), { number: row.ticket_number })}
        </span>
        <TicketKindBadge kind={row.kind} size="sm" />
        <NamedOnBadge namedAs={row.named_as} />
      </span>
      <span className="text-fg font-medium break-words">
        <TranslatableTextView value={row.subject} hideBadge />
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <TicketStatusBadge status={row.status} kind={row.kind} />
        <Typography as="span" variant="caption" tone="muted">
          {t("namedOn.raisedBy", { customer: row.requester.display_name })}
        </Typography>
        <Typography as="span" variant="caption" tone="muted" className="ml-auto">
          <RelativeTime value={row.updated_at} />
        </Typography>
      </span>
    </Link>
  );
}
